import { EntryLink } from '@/components/entry-link';
import { ThemedImg } from '@/components/landingpage/themed-img';
import { Button } from '@/components/ui/button';
import { useLocalizedRoute } from '@/hooks/use-localized-route';
import { type SharedData } from '@/types';
import { usePage } from '@inertiajs/react';
import { Trans, useTranslation } from 'react-i18next';

export interface TrustPartnerLogo {
    alt: string;
    src: string;
    darkSrc?: string;
    className?: string;
    heightClass?: string;
}

interface TrustbarProps {
    customDescription?: string;
    customButtonText?: string;
    customButtonAction?: () => void;
    customButtonClassName?: string;
    partnerLogos?: TrustPartnerLogo[];
}

export default function Trustbar({ customDescription, customButtonText, customButtonAction, customButtonClassName, partnerLogos }: TrustbarProps) {
    const { t } = useTranslation();
    const route = useLocalizedRoute();
    const { features, appearance, colorScheme } = usePage<SharedData>().props;
    const hausUndGrundEnabled = features['haus-und-grund'] ?? false;

    const defaultTrustLogos: TrustPartnerLogo[] = [
        { alt: t('home.trustbar.partners.logos.volksbank'), src: '/assets/logos/volksbank.svg' },
        { alt: t('home.trustbar.partners.logos.aareal'), src: '/assets/logos/aareal.svg', darkSrc: '/assets/logos/aareal-white.svg' },
        ...(hausUndGrundEnabled
            ? [
                  {
                      alt: t('home.trustbar.partners.logos.hausUndGrund'),
                      src: '/assets/logos/haus-und-grund.svg',
                      darkSrc: '/assets/logos/haus-und-grund-white.svg',
                      className: 'max-w-48',
                      heightClass: 'h-20',
                  },
              ]
            : []),
    ];

    const trustLogos = partnerLogos ?? defaultTrustLogos;

    return (
        <div className="landing-defer-paint bg-linear-to-br from-gray-50 to-white py-12 sm:py-16 lg:py-24 dark:from-gray-950 dark:to-gray-900">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                {/* Header Section */}
                <div className="mb-16 text-center">
                    <h2 className="text-base/7 font-semibold text-hs-blue dark:text-hs-green-light">{t('home.trustbar.subtitle')}</h2>
                    <h2 className="text-4xl font-semibold tracking-tight text-headline sm:text-5xl">{t('home.trustbar.title')}</h2>
                </div>

                <div className="grid grid-cols-1 items-start gap-x-8 gap-y-16 lg:grid-cols-2">
                    {/* Left Column - Description */}
                    <div className="mx-auto flex h-full w-full max-w-xl flex-col justify-between lg:mx-0">
                        <p className="text-lg text-paragraph">
                            {customDescription ? (
                                customDescription
                            ) : (
                                <Trans
                                    i18nKey="home.trustbar.description"
                                    components={{
                                        1: (
                                            <a
                                                href="https://plusforta.de"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-semibold text-hs-blue hover:underline dark:text-hs-green-light"
                                            />
                                        ),
                                    }}
                                />
                            )}
                        </p>

                        {/* Partner Logos */}
                        <div className="mt-10">
                            <div className="mb-4">
                                <p className="text-sm font-medium tracking-wide text-paragraph uppercase">{t('home.trustbar.partners.title')}</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-6">
                                {trustLogos.map((logo, index) => {
                                    const heightClass = logo.heightClass ?? 'h-12';
                                    const sharedLogoClasses = `${heightClass} w-auto object-contain opacity-70 grayscale brightness-90 contrast-75 transition-all duration-300 hover:opacity-100 hover:grayscale-0 hover:brightness-100 hover:contrast-100 ${logo.className ?? 'max-w-32'}`;

                                    return (
                                        <div key={index} className="group relative shrink-0">
                                            {logo.darkSrc ? (
                                                <ThemedImg
                                                    alt={logo.alt}
                                                    src={logo.src}
                                                    darkSrc={logo.darkSrc}
                                                    appearance={appearance}
                                                    initialDark={colorScheme === 'dark'}
                                                    className={sharedLogoClasses}
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                            ) : (
                                                <img
                                                    alt={logo.alt}
                                                    src={logo.src}
                                                    className={`${sharedLogoClasses} dark:invert dark:hover:invert-0`}
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Trust Features List */}
                    <div className="mx-auto w-full max-w-xl lg:mx-0">
                        <ul className="space-y-4 text-lg text-paragraph">
                            {(Array.isArray(t('home.trustbar.list', { returnObjects: true }))
                                ? (t('home.trustbar.list', { returnObjects: true }) as string[])
                                : []
                            ).map((item, index) => (
                                <li key={index} className="flex items-start gap-x-3">
                                    <svg
                                        className="mt-0.5 h-6 w-5 flex-none text-hs-blue dark:text-hs-green-light"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <span className="leading-relaxed">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* CTA Button - Centered below grid */}
                <div className="mt-16 text-center">
                    {customButtonAction ? (
                        <button
                            onClick={customButtonAction}
                            className={
                                customButtonClassName ||
                                'group relative cursor-pointer overflow-hidden rounded-md bg-linear-to-r from-hs-blue to-hs-green px-6 py-3 font-bold text-white duration-300 hover:scale-105'
                            }
                        >
                            <span className="absolute inset-0 -translate-x-full rotate-45 bg-white opacity-30 blur-sm transition-transform duration-500 group-hover:translate-x-full" />
                            <span className="relative z-10">{customButtonText || t('home.trustbar.cta')}</span>
                        </button>
                    ) : (
                        <Button
                            variant="default"
                            size="lg"
                            className="text-md transform px-8 py-3 font-medium shadow-lg transition-all duration-200 hover:scale-103 hover:shadow-xl"
                            asChild
                        >
                            <EntryLink href={route('register')}>{customButtonText || t('home.trustbar.cta')}</EntryLink>
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
